// Fiche d'un courrier : identité, historique, pièces, et actions du circuit (R2) selon l'état et
// les droits : affectation, transmission, réponse, validation, signature, envoi, classement.
import { LIBELLES_CANAUX_COURRIER, LIBELLES_CONFIDENTIALITE, LIBELLES_ETATS_COURRIER, type CourrierDetail } from '@cc/contrats/ui-metier';
import { LienBouton, Panneau, PuceEtat } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useParams } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useServices, type Service } from '../demarches/contenu';
import { ActionMetier, type ProprietesActionMetier } from './ActionMetier';
import { Historique, Pieces } from './Fiches';
import { puceCourrier } from './PageCourriers';
import commun from '../../commun/commun.module.css';
import styles from './metier.module.css';

type Def = Omit<ProprietesActionMetier, 'chemin' | 'invalider'> & { code: string; etats: string[]; droit: string };

const ACTIONS: Def[] = [
  { code: 'affecter', libelle: 'Affecter', titre: 'Affecter le courrier', explication: 'Le courrier est confié à un service, qui en devient responsable.', etats: ['enregistre', 'affecte'], droit: 'modifier', champs: [{ nom: 'service_id', libelle: 'Service', obligatoire: true }, { nom: 'motif', libelle: 'Instruction', multiligne: true }], succes: 'Courrier affecté.' },
  { code: 'transmettre', libelle: 'Transmettre', titre: 'Transmettre le courrier', explication: 'Le courrier est transmis pour traitement ou avis.', etats: ['affecte', 'en_traitement'], droit: 'transmettre', champs: [{ nom: 'service_id', libelle: 'Service destinataire', obligatoire: true }, { nom: 'motif', libelle: 'Commentaire', multiligne: true }], succes: 'Courrier transmis.' },
  { code: 'repondre', libelle: 'Préparer la réponse', titre: 'Préparer une réponse', explication: 'Un courrier départ lié est créé en brouillon ; il suivra le circuit de validation et de signature.', etats: ['affecte', 'en_traitement'], droit: 'creer', champs: [{ nom: 'objet', libelle: 'Objet de la réponse' }, { nom: 'canal', libelle: 'Canal prévu (papier, courriel, portail, main_propre, poste)' }], succes: 'Réponse créée en brouillon.' },
  { code: 'valider', libelle: 'Valider', titre: 'Valider le courrier', explication: 'Le courrier départ est validé et peut être signé.', etats: ['brouillon', 'en_validation'], droit: 'valider', champs: [{ nom: 'motif', libelle: 'Observation', multiligne: true }], succes: 'Courrier validé.' },
  { code: 'signer', libelle: 'Signer', titre: 'Signer le courrier', explication: 'La signature est enregistrée à votre nom, ou au titre de votre délégation de signature.', etats: ['valide'], droit: 'signer', succes: 'Courrier signé.' },
  { code: 'envoyer', libelle: 'Envoyer', titre: 'Enregistrer l\'envoi', explication: 'L\'envoi est enregistré avec son canal et sa preuve éventuelle.', etats: ['signe'], droit: 'transmettre', champs: [{ nom: 'canal', libelle: 'Canal (papier, courriel, portail, main_propre, poste)', obligatoire: true }, { nom: 'preuve', libelle: 'Preuve d\'envoi (référence, bordereau, accusé)', obligatoire: true }], succes: 'Envoi enregistré.' },
  { code: 'classer', libelle: 'Classer', titre: 'Classer le courrier', explication: 'Le courrier est classé sans réponse.', etats: ['affecte', 'en_traitement', 'repondu'], droit: 'modifier', champs: [{ nom: 'motif', libelle: 'Motif du classement', obligatoire: true, multiligne: true }], succes: 'Courrier classé.', variante: 'secondaire' },
];

/** Actions qui ne sont pas des transitions du workflow (route dédiée) : proposées selon l'état du courrier. */
const HORS_WORKFLOW = new Set(['repondre']);

/**
 * Service choisi dans la liste des services actifs (GET /services) : à l'affectation, et à la transmission d'un
 * courrier arrivée vers un autre service que le sien. Un courrier départ est transmis en validation, sans service.
 */
function avecServices(a: Def, c: CourrierDetail, services: Service[] | undefined): Def {
  if (a.code !== 'affecter' && a.code !== 'transmettre') return a;
  if (c.sens === 'depart') {
    return { ...a, explication: 'Le courrier départ est transmis en validation.', champs: a.champs?.filter((x) => x.nom !== 'service_id') };
  }
  const options = (services ?? []).filter((s) => a.code === 'affecter' || s.id !== c.service_affectataire?.id).map((s) => ({ valeur: s.id, libelle: s.nom }));
  const aide = options.length === 0 ? 'Liste des services indisponible : saisir l\'identifiant du service.' : undefined;
  return { ...a, champs: a.champs?.map((x) => (x.nom === 'service_id' ? { ...x, options, aide } : x)) };
}

export function PageCourrier() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const services = useServices();
  const courrier = useQuery({
    queryKey: ['courriers', 'fiche', id],
    queryFn: ({ signal }) => requete<CourrierDetail>(`/courriers/${id}`, { signal }),
    retry: false,
  });
  const c = courrier.data;
  const invalider = [['courriers'], [`/courriers/${id}/historique`], ['indicateurs']];
  const disponibles = c
    ? ACTIONS.filter((a) => (c.transitions_possibles.length && !HORS_WORKFLOW.has(a.code) ? c.transitions_possibles.some((t) => t.code === a.code) : a.etats.includes(c.etat)) && peut(contexte, 'courrier', a.droit))
    : [];

  return (
    <>
      <EnTetePage
        etiquette={c ? (c.sens === 'arrivee' ? 'Courrier arrivée' : 'Courrier départ') : 'Courrier'}
        titre={c ? `${c.numero ?? 'Sans numéro'} : ${c.objet}` : 'Courrier'}
        actions={<LienBouton href="/courriers" icone={ArrowLeft}>Registre</LienBouton>}
      />
      <EtatRequete chargement={courrier.isPending} erreur={courrier.error} onReessayer={() => void courrier.refetch()} lot="L0-C" fonction="La fiche du courrier">
        {c ? (
          <div className={commun.deuxColonnes}>
            <div className={styles.pile}>
              <Panneau titre="Identification">
                <dl className={commun.definitions}>
                  <dt>Numéro</dt>
                  <dd className={commun.reference}>{c.numero ?? 'non attribué'}</dd>
                  <dt>État</dt>
                  <dd>{puceCourrier(c)} <span className={commun.na}>({LIBELLES_ETATS_COURRIER[c.etat] ?? c.etat})</span></dd>
                  <dt>{c.sens === 'arrivee' ? 'Expéditeur' : 'Destinataire'}</dt>
                  <dd>{c.correspondant_nom}{c.correspondant_adresse ? `, ${c.correspondant_adresse}` : ''}</dd>
                  <dt>Date du courrier</dt>
                  <dd>{date(c.date_courrier)}</dd>
                  <dt>Enregistré le</dt>
                  <dd>{date(c.date_enregistrement)} ({LIBELLES_CANAUX_COURRIER[c.canal] ?? c.canal})</dd>
                  <dt>Service</dt>
                  <dd>{c.service_affectataire?.nom ?? 'non affecté'}</dd>
                  <dt>Échéance interne</dt>
                  <dd>{date(c.echeance_interne)}</dd>
                  <dt>Confidentialité</dt>
                  <dd>{c.confidentialite !== 'ordinaire' ? <PuceEtat ton="neutre" libelle={LIBELLES_CONFIDENTIALITE[c.confidentialite] ?? c.confidentialite} /> : 'Diffusion ordinaire'}</dd>
                  {c.reponse_a_courrier_id ? (
                    <>
                      <dt>En réponse à</dt>
                      <dd><a href={`/courriers/${c.reponse_a_courrier_id}`}>{c.reponse_a_numero ?? 'Courrier d\x27origine'}</a></dd>
                    </>
                  ) : null}
                  {c.reponses.length > 0 ? (
                    <>
                      <dt>Réponses</dt>
                      <dd>{c.reponses.map((r) => <a key={r.id} href={`/courriers/${r.id}`} style={{ marginRight: 8 }}>{r.numero} ({LIBELLES_ETATS_COURRIER[r.etat] ?? r.etat})</a>)}</dd>
                    </>
                  ) : null}
                  {c.envoi ? (
                    <>
                      <dt>Envoi</dt>
                      <dd>{LIBELLES_CANAUX_COURRIER[c.envoi.canal] ?? c.envoi.canal}, le {date(c.envoi.envoye_le)} : {c.envoi.preuve}</dd>
                    </>
                  ) : null}
                </dl>
              </Panneau>
              <Panneau titre="Actions">
                {disponibles.length === 0 ? (
                  <p>Aucune action ne vous est ouverte sur ce courrier dans son état actuel.</p>
                ) : (
                  <div className={styles.actions}>
                    {disponibles.map((a) => (
                      <ActionMetier key={a.code} {...avecServices(a, c, services.data)} chemin={`/courriers/${c.id}/${a.code}`} invalider={invalider} />
                    ))}
                  </div>
                )}
              </Panneau>
              <Panneau titre="Pièces">
                <Pieces objetType="courrier" objetId={c.id} peutDeposer={peut(contexte, 'courrier', 'modifier')} />
              </Panneau>
            </div>
            <Panneau titre="Historique">
              <Historique chemin={`/courriers/${c.id}/historique`} libelles={LIBELLES_ETATS_COURRIER} />
            </Panneau>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
